import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { DeleteFilled, EditFilled, QuestionCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import ImageRender from '../../utils/ImageRender';

// Actions
import { get_all_categories, set_category_create_id, category_delete } from '../../../redux/actions/categoriesActions';

const CategoriesTable = (props) => {
	const { messageApi, setIsModalOpen, setUpdate, setCategoryToUpdate, pageSize, setPageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_categories_loading, all_categories, category_delete_loading } = useSelector(state => state.categories);

	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		dispatch(set_category_create_id(record._id.$oid));
		setCategoryToUpdate(record);
	}

	const deleteCategory = (record) => {
		let newSkip = skip;

		if ((all_categories.count - 1) % pageSize === 0) {
			newSkip -= pageSize;
			setSkip(newSkip);
			localStorage.setItem('categoriesSkip', newSkip);
		}

		const filters = { skip: newSkip, limit: pageSize };
		dispatch(category_delete(messageApi, record._id.$oid, filters));
	}

	const columns = [
		{
			key: 'image',
			title: t('form.label.image'),
			dataIndex: 'image',
			render: (_, record) => (
				<ImageRender
					key={record.image}
					url={`${process.env.REACT_APP_SERVER_URL}/api/recipes/categories/${record._id.$oid}/asset`}
					size={{ width: 50, height: 50 }}
				/>
			)
		},
		{
			key: 'name',
			title: t('form.label.name'),
			dataIndex: 'name',
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('table.copyableText.copyId'), t('table.copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		{
			key: 'description',
			title: t('form.label.description'),
			dataIndex: 'description'
		},
		{
			key: 'action',
			title: t('table.headers.actions'),
			dataIndex: 'action',
			render: (_, record) => (
				<Space>
					<Tooltip title={t('buttons.edit')}>
						<Button
							type='primary'
							style={{ backgroundColor: '#80B4B9' }}
							shape='circle'
							size='large'
							icon={<EditFilled />}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('ingredients.categories.delete.confirmTitle')}
						description={t('ingredients.categories.delete.confirmText')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteCategory(record)}
						okButtonProps={{
							loading: category_delete_loading
						}}
						okText={t('buttons.confirm')}
						cancelText={t('buttons.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<DeleteFilled />}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pagination.pageSize;
		setSkip(skip);
		localStorage.setItem('categoriesSkip', skip);

		const limit = pagination.pageSize;
		setPageSize(limit);
		localStorage.setItem('categoriesPageSize', limit);

		const categoriesFilters = { skip, limit };
		dispatch(get_all_categories(categoriesFilters));
	};

	return (
		<Spin spinning={all_categories_loading}>
			<Table
				columns={columns}
				dataSource={all_categories.categories}
				onChange={handleTableChange}
				scroll={{
					x: 'auto'
				}}
				pagination={{
					pageSize: pageSize, 
					total: all_categories.count,
					current: (skip / pageSize) + 1,
					showSizeChanger: true,
					showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('table.pagination.of')} ${total}`,
					showTitle: false,
					responsive: true,
					locale: { items_per_page: `/ ${t('table.pagination.page')}` }
				}}
			/>
		</Spin>
	)
}

CategoriesTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setCategoryToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	setPageSize: PropTypes.func.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default CategoriesTable;
